import { supabase } from '@/lib/supabaseClient';
import { UserProfile, UserRole } from '@/types/auth';

/**
 * Service for Supabase Authentication and Profile Management.
 */
export const authService = {
  /**
   * Signs in user with email and password.
   */
  async signIn(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    return data;
  },

  /**
   * Signs out current user and clears session.
   */
  async signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  /**
   * Sends password reset email.
   */
  async resetPassword(email: string) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/login?reset=true`,
    });
    if (error) throw error;
  },

  /**
   * Retrieves profile for a specific user ID.
   */
  async getProfile(userId: string): Promise<UserProfile | null> {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, email, full_name, role, is_active, avatar_url, permissions, created_at, updated_at')
      .eq('id', userId)
      .single();

    if (error) {
      console.warn(`[authService] Could not fetch profile for user ${userId}:`, error.message);
      return null;
    }

    return {
      id: data.id,
      email: data.email,
      full_name: data.full_name,
      role: (data.role as UserRole) || 'viewer',
      is_active: data.is_active ?? true,
      avatar_url: data.avatar_url,
      permissions: data.permissions || null,
      created_at: data.created_at,
      updated_at: data.updated_at,
    };
  },

  /**
   * Gets the currently authenticated user session.
   */
  async getSession() {
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    return data.session;
  },

  /**
   * Invites a new user via Supabase Edge Function using auth.admin.inviteUserByEmail.
   * Only callable by an active VSB administrator.
   */
  async inviteUser(params: {
    email: string;
    fullName: string;
    role: UserRole;
    redirectTo?: string;
  }) {
    const { data, error } = await supabase.functions.invoke('invite-user', {
      body: {
        email: params.email,
        fullName: params.fullName,
        role: params.role,
        redirectTo: params.redirectTo || `${window.location.origin}/login`,
      },
    });

    if (error) {
      throw new Error(error.message || 'Failed to send user invitation.');
    }
    if (data?.error) {
      throw new Error(data.error);
    }
    return data;
  },

  /**
   * Updates the authenticated user's password (e.g. on accepting invitation).
   */
  async updatePassword(password: string) {
    const { data, error } = await supabase.auth.updateUser({
      password,
    });
    if (error) throw error;
    return data;
  },
};
